import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import SidebarLeft from '../components/SidebarLeft';
import SidebarRight from '../components/SidebarRight';
import ThreadCard from '../components/ThreadCard';
import CreateThreadModal from '../components/CreateThreadModal';
import { getApiUrl } from '../config';

const POSTS_PER_PAGE = 15;

// Demo data for when backend is not connected
const demoThreads = [
  {
    _id: '1',
    title: "Why does my transformer model's attention weights become uniform after fine-tuning?",
    excerpt: "I've been fine-tuning a BERT model for classification and noticed the attention weights become nearly uniform across all tokens. The model still performs well on downstream tasks, but this seems counterintuitive...",
    type: 'question',
    category: 'machine-learning',
    currentVersion: 'v2.3',
    voteCount: 247,
    replyCount: 42,
    views: 1200,
    author: {
      displayName: 'Tara Okonkwo',
      role: 'expert'
    },
    codeBlocks: [{
      language: 'python',
      code: `def analyze_attention(model, input_ids):
    outputs = model(input_ids, output_attentions=True)
    attention = outputs.attentions[-1]  # Last layer
    return attention.mean(dim=1)  # Average heads`
    }],
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: '2',
    title: 'Building a real-time collaborative code editor with CRDTs',
    excerpt: "I've been working on a multiplayer code editor similar to VS Code Live Share. Here's my implementation using Yjs for CRDT-based sync and how I solved the cursor position conflicts...",
    type: 'project',
    category: 'web-dev',
    currentVersion: 'v1.4.2',
    voteCount: 189,
    replyCount: 78,
    views: 2400,
    author: {
      displayName: 'Jamie Thornton',
      role: 'maintainer'
    },
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: '3',
    title: 'The case for Rust in production ML systems',
    excerpt: "After migrating our inference pipeline from Python to Rust, we saw 40% latency reduction and 60% memory savings. But the real benefits were in reliability and deployment simplicity...",
    type: 'discussion',
    category: 'systems',
    voteCount: 156,
    replyCount: 124,
    views: 3800,
    author: {
      displayName: 'Alex Nakamura',
      role: 'user'
    },
    createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: '4',
    title: 'Experimenting with mixture-of-experts for small language models',
    excerpt: "I've been testing MoE architectures on models under 1B parameters. Surprisingly, using 8 experts with 2 active per token gives better perplexity than a dense model of similar compute...",
    type: 'experiment',
    category: 'machine-learning',
    currentVersion: 'v0.2.1',
    voteCount: 98,
    replyCount: 56,
    views: 892,
    author: {
      displayName: 'Maya Lindgren',
      role: 'user'
    },
    codeBlocks: [{
      language: 'python',
      code: `class MoELayer(nn.Module):
    def __init__(self, n_experts=8, top_k=2):
        self.experts = nn.ModuleList([Expert() for _ in range(n_experts)])
        self.gate = nn.Linear(d_model, n_experts)`
    }],
    createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: '5',
    title: 'Implementing zero-knowledge proofs for private smart contracts',
    excerpt: "I've been exploring zkSNARKs for building private DeFi applications. Here's a breakdown of my approach using circom and snarkjs...",
    type: 'project',
    category: 'security',
    currentVersion: 'v0.1.0',
    voteCount: 134,
    replyCount: 45,
    views: 1560,
    author: {
      displayName: 'Zara Chen',
      role: 'expert'
    },
    createdAt: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: '6',
    title: 'Building a real-time analytics pipeline with Apache Kafka and Flink',
    excerpt: "Our team recently migrated from batch processing to stream processing. Here's how we handle 10M events/second with sub-second latency...",
    type: 'discussion',
    category: 'data-science',
    voteCount: 112,
    replyCount: 67,
    views: 2100,
    author: {
      displayName: 'Priya Venkatesh',
      role: 'maintainer'
    },
    createdAt: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString()
  }
];

const Feed = ({ openAuthModal, showToast, searchQuery }) => {
  const { user, token } = useAuth();
  const [threads, setThreads] = useState([]);
  const [allThreads, setAllThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState('for-you');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeFilter, setActiveFilter] = useState('home');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [savedThreadIds, setSavedThreadIds] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const pageRef = useRef(1);

  // Fetch saved thread IDs for the user
  useEffect(() => {
    const fetchSavedThreads = async () => {
      if (user && token) {
        try {
          const response = await fetch(getApiUrl('/api/users/saved/threads'), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            const savedIds = data.threads?.map(t => t._id) || [];
            setSavedThreadIds(savedIds);
            
            // If saved filter is active, set the saved threads directly
            if (activeFilter === 'saved' && data.threads) {
              setAllThreads(data.threads);
            }
          }
        } catch (error) {
          console.log('Failed to fetch saved threads');
        }
      }
    };
    fetchSavedThreads();
  }, [user, token, activeFilter]);

  // Reset pagination when filters change
  useEffect(() => {
    pageRef.current = 1;
    setHasMore(true);
    setAllThreads([]);
  }, [activeCategory, activeFilter, activeTab, searchQuery]);

  // Fetch threads from API
  const fetchThreads = useCallback(async (pageNum = 1, append = false) => {
    // Skip fetching if saved filter is active - handled separately
    if (activeFilter === 'saved') {
      if (!user || !token) {
        showToast('Sign in to view saved threads');
        setActiveFilter('home');
        return;
      }
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      
      try {
        const response = await fetch(getApiUrl('/api/users/saved/threads'), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const savedIds = data.threads?.map(t => t._id) || [];
          setSavedThreadIds(savedIds);
          setAllThreads(data.threads || []);
          setHasMore(false); // Saved threads don't paginate
        } else {
          setAllThreads([]);
          setHasMore(false);
        }
      } catch (error) {
        console.log('Failed to fetch saved threads');
        setAllThreads([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
      return;
    }
    
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      
      // Build query params
      const params = new URLSearchParams();
      params.append('page', pageNum.toString());
      params.append('limit', POSTS_PER_PAGE.toString());
      if (activeCategory) params.append('category', activeCategory);
      if (searchQuery) params.append('search', searchQuery);
      
      // Determine sort
      if (activeTab === 'trending') {
        params.append('sort', 'trending');
      } else if (activeFilter === 'popular') {
        params.append('sort', 'top');
      } else if (activeFilter === 'recent') {
        params.append('sort', 'recent');
      } else {
        params.append('sort', 'hot'); // Home uses hot score
      }

      const response = await fetch(getApiUrl(`/api/threads?${params.toString()}`));
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched threads:', data.threads?.length || 0, 'threads');
        if (data.threads && data.threads.length > 0) {
          if (append) {
            setAllThreads(prev => [...prev, ...data.threads]);
          } else {
            setAllThreads(data.threads);
          }
          // Check if there are more threads
          setHasMore(data.threads.length >= POSTS_PER_PAGE);
        } else {
          // No threads found - show empty state
          if (!append) setAllThreads([]);
          setHasMore(false);
        }
      } else {
        // Log error for debugging
        console.error('Failed to fetch threads:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error details:', errorData);
        if (!append) setAllThreads([]);
        setHasMore(false);
      }
    } catch (error) {
      // API call failed - show empty state instead of demo data
      console.error('Error fetching threads:', error);
      if (!append) setAllThreads([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeCategory, activeFilter, activeTab, searchQuery, user, token, showToast]);

  // Initial fetch when filters change
  useEffect(() => {
    console.log('Fetching threads with filters:', { activeCategory, activeFilter, activeTab, searchQuery });
    fetchThreads(1, false);
  }, [activeCategory, activeFilter, activeTab, searchQuery, fetchThreads]);

  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    if (hasMore && !loading && !loadingMore) {
      pageRef.current += 1;
      fetchThreads(pageRef.current, true);
    }
  }, [hasMore, loading, loadingMore, fetchThreads]);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const currentLoader = loaderRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [loadMore]);

  // Filter threads based on selections
  useEffect(() => {
    let filtered = [...allThreads];
    console.log('Filtering threads. allThreads:', allThreads.length, 'filtered:', filtered.length);

    // Filter by category (for saved mode, we also apply category filter)
    if (activeCategory) {
      filtered = filtered.filter(t => t.category === activeCategory);
      console.log('After category filter:', filtered.length);
    }

    // The sorting is now handled by the API, but we keep local sorting for demo data
    if (activeFilter === 'popular') {
      filtered = filtered.sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
    } else if (activeFilter === 'recent') {
      filtered = filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    // Sort by tab selection
    if (activeTab === 'trending') {
      filtered = filtered.sort((a, b) => {
        const scoreA = (a.voteCount || 0) + (a.views || 0) / 100;
        const scoreB = (b.voteCount || 0) + (b.views || 0) / 100;
        return scoreB - scoreA;
      });
    } else if (activeTab === 'following') {
      if (!user) {
        showToast('Sign in to see posts from people you follow');
      }
    }

    console.log('Final filtered threads:', filtered.length);
    setThreads(filtered);
  }, [allThreads, activeCategory, activeFilter, activeTab, user, showToast]);

  const handleNewThread = () => {
    if (!user) {
      showToast('Sign in to create a thread');
      openAuthModal('signin');
      return;
    }
    setCreateModalOpen(true);
  };

  const handleThreadCreated = (newThread) => {
    setAllThreads([newThread, ...allThreads]);
  };

  const handleSaveToggle = (threadId, saved) => {
    if (saved) {
      setSavedThreadIds(prev => [...prev, threadId]);
    } else {
      setSavedThreadIds(prev => prev.filter(id => id !== threadId));
    }
  };

  return (
    <div className="main-container">
      <SidebarLeft 
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
      />

      <main className="feed">
        <div className="feed-header">
          <div className="feed-tabs">
            <button 
              className={`feed-tab ${activeTab === 'for-you' ? 'active' : ''}`}
              onClick={() => setActiveTab('for-you')}
            >
              For You
            </button>
            <button 
              className={`feed-tab ${activeTab === 'following' ? 'active' : ''}`}
              onClick={() => setActiveTab('following')}
            >
              Following
            </button>
            <button 
              className={`feed-tab ${activeTab === 'trending' ? 'active' : ''}`}
              onClick={() => setActiveTab('trending')}
            >
              Trending
            </button>
          </div>
          <button className="btn btn-accent" onClick={handleNewThread}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Thread
          </button>
        </div>

        {/* Active filter indicator */}
        {(activeCategory || activeFilter !== 'home' || searchQuery) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            padding: '12px 16px',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '14px',
            color: 'var(--text-secondary)'
          }}>
            <span>Showing:</span>
            {activeCategory && (
              <span style={{
                background: 'var(--accent-bg)',
                color: 'var(--accent)',
                padding: '4px 10px',
                borderRadius: '6px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {activeCategory.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                <button 
                  onClick={() => setActiveCategory(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    color: 'var(--accent)'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </span>
            )}
            {activeFilter !== 'home' && (
              <span style={{
                background: 'var(--purple-bg)',
                color: 'var(--purple)',
                padding: '4px 10px',
                borderRadius: '6px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}
                <button 
                  onClick={() => setActiveFilter('home')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    color: 'var(--purple)'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </span>
            )}
            {searchQuery && (
              <span style={{
                background: 'var(--success-bg)',
                color: 'var(--success)',
                padding: '4px 10px',
                borderRadius: '6px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Search: "{searchQuery}"
              </span>
            )}
            <span style={{ marginLeft: 'auto', color: 'var(--text-tertiary)' }}>
              {threads.length} {threads.length === 1 ? 'thread' : 'threads'}
            </span>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
            <div className="loading-spinner" style={{
              width: '24px',
              height: '24px',
              border: '2px solid var(--border)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 12px'
            }} />
            Loading threads...
          </div>
        ) : threads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-tertiary)' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '16px', opacity: 0.5 }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p>No threads found</p>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>Try a different filter or category</p>
          </div>
        ) : (
          <>
            {threads.map(thread => (
              <ThreadCard 
                key={thread._id} 
                thread={thread} 
                showToast={showToast}
                openAuthModal={openAuthModal}
                isSavedProp={savedThreadIds.includes(thread._id)}
                onSaveToggle={handleSaveToggle}
              />
            ))}
            
            {/* Infinite scroll loader */}
            <div 
              ref={loaderRef} 
              style={{ 
                padding: '24px', 
                textAlign: 'center',
                color: 'var(--text-tertiary)'
              }}
            >
              {loadingMore ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid var(--border)',
                    borderTopColor: 'var(--accent)',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  Loading more...
                </div>
              ) : hasMore ? (
                <button 
                  onClick={loadMore}
                  style={{ 
                    fontSize: '13px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    padding: '10px 20px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'var(--bg-tertiary)'}
                  onMouseOut={(e) => e.target.style.background = 'var(--bg-secondary)'}
                >
                  Load More
                </button>
              ) : threads.length > 0 ? (
                <span style={{ fontSize: '13px' }}>You've reached the end</span>
              ) : null}
            </div>
          </>
        )}
      </main>

      <SidebarRight />

      <CreateThreadModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        showToast={showToast}
        onThreadCreated={handleThreadCreated}
      />
    </div>
  );
};

export default Feed;

